import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ChartSummary } from '@models/chart-summary.interface';
import { ChartsService } from 'src/app/services/charts.service';
import { ChartListComponent } from './chart-list/chart-list.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-charts-page',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, ChartListComponent],
  templateUrl: './charts-page.component.html',
  styleUrl: './charts-page.component.scss'
})
export class ChartsPageComponent implements OnInit {
  private readonly chartsService = inject(ChartsService);
  private readonly dialog = inject(MatDialog);

  protected charts = signal<ChartSummary[]>([]);
  protected selectedChart = signal<ChartSummary | null>(null);
  protected isLoadingList = signal(false);
  protected isSaving = signal(false);
  protected chartName = signal('');
  protected selectedFile = signal<File | null>(null);
  protected uploadError = signal<string | null>(null);

  ngOnInit(): void {
    this.loadList();
  }

  protected onChartSelected(chart: ChartSummary): void {
    this.selectedChart.set(chart);
  }

  protected onChartDeleted(chartId: number): void {
    const chart = this.charts().find(item => item.id === chartId);
    if (!chart) return;

    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      {
        data: {
          title: 'Delete Chart',
          primaryText: `Are you sure you want to delete ${chart.chartName}?`,
          confirmText: 'Delete'
        },
        width: '420px',
        disableClose: true
      }
    );

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      const selectedId = this.selectedChart()?.id === chartId
        ? undefined
        : this.selectedChart()?.id;

      this.chartsService.delete(chartId).subscribe({
        next: () => this.loadList(selectedId, true),
        error: () => alert('Failed to delete chart. Please try again.')
      });
    });
  }

  protected onChartNameChanged(event: Event): void {
    this.chartName.set((event.target as HTMLInputElement).value);
    this.uploadError.set(null);
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile.set(input.files?.[0] ?? null);
    this.uploadError.set(null);
  }

  protected onChartCreated(): void {
    const name = this.chartName().trim();
    const file = this.selectedFile();
    if (!name || !file) {
      this.uploadError.set('Enter a chart name and select a CSV file.');
      return;
    }

    this.isSaving.set(true);
    this.uploadError.set(null);
    this.chartsService.create(name, file).subscribe({
      next: createdChart => {
        this.isSaving.set(false);
        this.chartName.set('');
        this.selectedFile.set(null);
        this.loadList(createdChart.id, true);
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.uploadError.set(this.getUploadError(error));
      }
    });
  }

  private loadList(selectId?: number, forceRefresh = false): void {
    this.isLoadingList.set(true);
    this.chartsService.getList(forceRefresh).subscribe({
      next: charts => {
        this.charts.set(charts);
        this.selectedChart.set(
          charts.find(chart => chart.id === selectId) ?? charts[0] ?? null
        );
        this.isLoadingList.set(false);
      },
      error: () => {
        this.charts.set([]);
        this.selectedChart.set(null);
        this.isLoadingList.set(false);
      }
    });
  }

  private getUploadError(error: HttpErrorResponse): string {
    if (typeof error.error?.error === 'string')
      return error.error.error;
    if (typeof error.error === 'string')
      return error.error;
    return 'The chart could not be uploaded.';
  }
}